import { setAccessToken, decodeToken, setCookie } from '@backrs_ui/common-ui';

import { getBackrsToken, isGuestToken } from './tokenHelpers';
import {
  SHARED_SESSION_GRANT_ID_KEY,
  SHARED_SESSION_RESOURCE_TYPE_KEY,
} from '../redux/sharedSession/sharedAccountSession';
import { ENVIRONMENT, GROW_URL } from '../settings';

export type DecodedToken = Readonly<{
  sub: string;
  emulator?: {
    user_id: string;
    first_name: string;
    last_name: string;
    session_id: string;
  };
}>;

export type EmulatorClaimResult = Readonly<{
  emulator?: {
    user_id: string;
    first_name: string;
    last_name: string;
    session_id: string;
  };
  exists: boolean;
  isValid: boolean;
  error?: unknown;
}>;

/**
 * Extracts and validates emulator claims from a JWT token
 * @param token - The JWT token to decode and validate
 * @returns EmulatorClaimResult with emulator data and validity flag
 */
export const extractEmulatorClaim = (token: string | null): EmulatorClaimResult => {
  if (!token) {
    return { exists: false, isValid: false };
  }

  try {
    const decodedToken = decodeToken(token);

    // Check if emulator claim exists at all
    if (!decodedToken.emulator) {
      return { exists: false, isValid: false };
    }

    // Emulator claim exists, now validate it
    if (
      decodedToken.emulator.user_id &&
      decodedToken.emulator.session_id &&
      decodedToken.emulator.first_name &&
      decodedToken.emulator.last_name
    ) {
      return {
        emulator: {
          user_id: decodedToken.emulator.user_id,
          first_name: decodedToken.emulator.first_name,
          last_name: decodedToken.emulator.last_name,
          session_id: decodedToken.emulator.session_id,
        },
        exists: true,
        isValid: true,
      };
    }

    // Emulator claim exists but is invalid
    return { exists: true, isValid: false };
  } catch (error) {
    return { exists: false, isValid: false, error };
  }
};

/** The emulator's own token, parked for the duration of the emulation. */
const RETURN_TOKEN_KEY = 'backrs_ui_return_token';

export const clearReturnToken = (): void => localStorage.removeItem(RETURN_TOKEN_KEY);

/** The parked token, only if it belongs to the emulator of the session in hand. */
export const readReturnToken = (): string | null => {
  const parked = localStorage.getItem(RETURN_TOKEN_KEY);

  if (!parked) {
    return null;
  }

  try {
    const { emulator } = extractEmulatorClaim(getBackrsToken());

    if (emulator && decodeToken(parked).sub === emulator.user_id) {
      return parked;
    }
  } catch (error) {
    console.error('Failed to decode parked return token', error);
  }

  return null;
};

const parkReturnToken = () => {
  const token = getBackrsToken();

  // a guest is no account to return to
  if (!token || isGuestToken(token)) {
    clearReturnToken();

    return;
  }

  const claim = extractEmulatorClaim(token);

  // a second hop must not overwrite the first hop's parked token
  if (claim.exists) {
    return;
  }

  // a token that won't decode can't be handed back
  if (claim.error) {
    clearReturnToken();

    return;
  }

  localStorage.setItem(RETURN_TOKEN_KEY, token);
};

export const exitEmulation = (returnToken: string): void => {
  setAccessToken('backrs_ui_token', returnToken);
  setCookie(returnToken, ENVIRONMENT);
  clearReturnToken();

  // the emulated user's shared-session grant must not follow the emulator back
  localStorage.removeItem(SHARED_SESSION_GRANT_ID_KEY);
  localStorage.removeItem(SHARED_SESSION_RESOURCE_TYPE_KEY);

  // reload rather than re-render so nothing fetched as the emulated user survives
  window.location.href = '/';
};

/**
 * Handles emulation errors by logging and invoking callback
 * @param error - The error that occurred
 * @param onEmulationError - Callback to handle the error (e.g., logout)
 */
export const handleEmulationError = (error: unknown, onEmulationError: () => void) => {
  console.error('Emulation URL processing failed:', error);
  onEmulationError();
};

/**
 * Processes an emulation URL, validates the token, and sets authentication
 * @param url - The emulation URL to process
 * @param onEmulationError - Callback to handle errors
 */
export const handleEmulationUrl = (url: string, onEmulationError: () => void) => {
  try {
    const [path, token] = url.split('#');
    const [route, search] = path.split('?');

    if (route !== '/emulation') {
      console.warn('Invalid emulation URL: ', path);

      return;
    }

    const kParam = new URLSearchParams(search).get('k');

    if (!kParam) {
      throw new Error('Missing required key parameter in URL');
    }

    if (!token) {
      throw new Error('No token found in emulation URL');
    }

    const emulatorResult = extractEmulatorClaim(token);

    if (emulatorResult.isValid && emulatorResult.emulator) {
      if (emulatorResult.emulator.session_id !== kParam) {
        throw new Error('Invalid url key');
      }

      parkReturnToken();
      setAccessToken('backrs_ui_token', token);
      setCookie(token, ENVIRONMENT);
    } else {
      throw new Error('Invalid emulator object');
    }
  } catch (error) {
    handleEmulationError(error, onEmulationError);
  } finally {
    // Always redirect to root after processing emulation URL
    window.location.href = '/';
  }
};

/**
 * Constructs emulation URL from token and redirects user
 * @param token - The emulation request response: {session_id: string, access_token: string}
 */
export const redirectToEmulation = (token: { session_id: string; access_token: string }): void => {
  const emulatorClaim = extractEmulatorClaim(token?.access_token);

  if (!emulatorClaim.isValid || !emulatorClaim.emulator) {
    throw new Error('Invalid emulation token');
  }

  const { session_id } = emulatorClaim.emulator;
  const emulationUrl = `${GROW_URL}/emulation?k=${session_id}#${token.access_token}`;

  window.location.href = emulationUrl;
};
